export class ConversationsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    async create(params) {
        return this.client.post('/api/conversations', params);
    }
    async list(limit, cursor) {
        const params = new URLSearchParams();
        if (limit)
            params.set('limit', limit.toString());
        if (cursor)
            params.set('cursor', cursor);
        const query = params.toString();
        return this.client.get(`/api/conversations${query ? `?${query}` : ''}`, {
            cache: 'no-store',
        });
    }
    async get(conversationId) {
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}`);
    }
    async updateName(conversationId, encryptedName, nameNonce, wrappedMetadataKeys, encryptedSystemEvent, options) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}`, {
            encryptedName,
            nameNonce,
            ...(wrappedMetadataKeys ? { wrappedMetadataKeys } : {}),
            ...(encryptedSystemEvent ?? {}),
            ...(options?.ciphertextRotation ? { ciphertextRotation: true } : {}),
        });
    }
    async updateMemberSettings(conversationId, encryptedMemberSettings, memberSettingsNonce, wrappedMetadataKeys) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/member-settings`, {
            encryptedMemberSettings,
            memberSettingsNonce,
            ...(wrappedMetadataKeys ? { wrappedMetadataKeys } : {}),
        });
    }
    async updateMetadataKeys(conversationId, wrappedMetadataKeys) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/metadata-keys`, { wrappedMetadataKeys });
    }
    async sendMessage(conversationId, params, requestOptions) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/messages`, params, requestOptions);
    }
    async getMessages(conversationId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        if (options?.direction)
            params.set('direction', options.direction);
        const query = params.toString();
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/messages${query ? `?${query}` : ''}`);
    }
    /**
     * Load a window around a message (newest-first), for reply navigation / deep links.
     */
    async getMessagesAround(conversationId, centerMessageId, options) {
        const params = new URLSearchParams();
        if (options?.before != null)
            params.set('before', String(options.before));
        if (options?.after != null)
            params.set('after', String(options.after));
        const query = params.toString();
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/messages/around/${encodeURIComponent(centerMessageId)}${query ? `?${query}` : ''}`);
    }
    /**
     * Load one message. Use `include=revisionHistory` to fetch E2E edit history blobs.
     */
    async getMessage(conversationId, messageId, options) {
        const params = new URLSearchParams();
        if (options?.include)
            params.set('include', options.include);
        const query = params.toString();
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}${query ? `?${query}` : ''}`);
    }
    /**
     * Thread root plus one page of replies (oldest-first). `messageId` may be
     * any message in the thread; `before` is the previous page's `cursor`.
     */
    async getThread(conversationId, messageId, options) {
        const params = new URLSearchParams();
        if (options?.before)
            params.set('before', options.before);
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        const query = params.toString();
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/thread${query ? `?${query}` : ''}`);
    }
    async editMessage(conversationId, messageId, params, requestOptions) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`, params, requestOptions);
    }
    async deleteMessageForSelf(conversationId, messageId) {
        return this.client.delete(`/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`);
    }
    async deleteMessageForEveryone(conversationId, messageId) {
        return this.client.delete(`/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/everyone`);
    }
    async addMember(conversationId, identityId, encryptedSystemEvent) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/members`, { identityId, ...(encryptedSystemEvent ?? {}) });
    }
    async removeMember(conversationId, identityId, encryptedSystemEvent) {
        return this.client.delete(`/api/conversations/${encodeURIComponent(conversationId)}/members/${encodeURIComponent(identityId)}`, encryptedSystemEvent ? { body: encryptedSystemEvent } : undefined);
    }
    async getFormerMembers(conversationId) {
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/former-members`);
    }
    async listPendingInvitesForConversation(conversationId) {
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/pending-invites`);
    }
    async revokeGroupInvite(conversationId, inviteId) {
        return this.client.delete(`/api/conversations/${encodeURIComponent(conversationId)}/invites/${encodeURIComponent(inviteId)}`);
    }
    async leave(conversationId, options) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/leave`, options ?? {});
    }
    async promoteToAdmin(conversationId, identityId, encryptedSystemEvent) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/admins`, { identityId, ...(encryptedSystemEvent ?? {}) });
    }
    async terminateGroup(conversationId) {
        return this.client.delete(`/api/conversations/${encodeURIComponent(conversationId)}`);
    }
    async listInvites(limit, cursor) {
        const params = new URLSearchParams();
        if (limit)
            params.set('limit', limit.toString());
        if (cursor)
            params.set('cursor', cursor);
        const query = params.toString();
        return this.client.get(`/api/conversations/invites${query ? `?${query}` : ''}`);
    }
    async acceptInvite(inviteId) {
        return this.client.post(`/api/conversations/invites/${encodeURIComponent(inviteId)}/accept`, {});
    }
    async declineInvite(inviteId) {
        return this.client.post(`/api/conversations/invites/${encodeURIComponent(inviteId)}/decline`, {});
    }
    async getInvitePreview(inviteId) {
        return this.client.get(`/api/conversations/invites/${encodeURIComponent(inviteId)}/preview`);
    }
    async updateGifsDisabled(conversationId, gifsDisabled, encryptedSystemEvent) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/gifs`, { gifsDisabled, ...(encryptedSystemEvent ?? {}) });
    }
    async updateGifContentFilter(conversationId, gifContentFilter, encryptedSystemEvent) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/gif-content-filter`, { gifContentFilter, ...(encryptedSystemEvent ?? {}) });
    }
    async updateCustomEmojisDisabled(conversationId, customEmojisDisabled, encryptedSystemEvent) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/custom-emojis`, { customEmojisDisabled, ...(encryptedSystemEvent ?? {}) });
    }
    async updateMessageSearchCachePolicy(conversationId, disallowPersistentMessageSearchCache, encryptedSystemEvent) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/message-search-cache`, { disallowPersistentMessageSearchCache, ...(encryptedSystemEvent ?? {}) });
    }
    async updateAllowSkipModeration(conversationId, allowSkipModeration, encryptedSystemEvent) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/allow-skip-moderation`, { allowSkipModeration, ...(encryptedSystemEvent ?? {}) });
    }
    async pinMessage(conversationId, messageId) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/pins`, { messageId });
    }
    async unpinMessage(conversationId, messageId) {
        return this.client.delete(`/api/conversations/${encodeURIComponent(conversationId)}/pins/${encodeURIComponent(messageId)}`);
    }
    async getPinnedMessages(conversationId, options) {
        const params = new URLSearchParams();
        if (options?.limit != null)
            params.set('limit', String(options.limit));
        if (options?.cursor)
            params.set('cursor', options.cursor);
        const query = params.toString();
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/pinned-messages${query ? `?${query}` : ''}`);
    }
    async getStats() {
        return this.client.get('/api/conversations/stats');
    }
    async listPreferences() {
        return this.client.get('/api/conversations/preferences', { cache: 'no-store' });
    }
    async updatePreferences(conversationId, patch) {
        return this.client.patch(`/api/conversations/preferences/${encodeURIComponent(conversationId)}`, patch);
    }
    // ---------------------------------------------------------------------------
    // Call endpoints
    // ---------------------------------------------------------------------------
    async initiateCall(conversationId, media) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/calls`, { media });
    }
    async joinCall(conversationId, callId, media) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/calls/${encodeURIComponent(callId)}/join`, { media });
    }
    async leaveCall(conversationId, callId) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/calls/${encodeURIComponent(callId)}/leave`, {});
    }
    async endCall(conversationId, callId) {
        return this.client.post(`/api/conversations/${encodeURIComponent(conversationId)}/calls/${encodeURIComponent(callId)}/end`, {});
    }
    async getActiveCall(conversationId) {
        return this.client.get(`/api/conversations/${encodeURIComponent(conversationId)}/calls/active`);
    }
    async updateCallMediaState(conversationId, callId, media) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/calls/${encodeURIComponent(callId)}/media`, { media });
    }
    async updateCallSettings(conversationId, settings, encryptedSystemEvent) {
        return this.client.patch(`/api/conversations/${encodeURIComponent(conversationId)}/call-settings`, { ...settings, ...(encryptedSystemEvent ?? {}) });
    }
}
//# sourceMappingURL=conversations-api.js.map